/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useState, useContext } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table as BTable } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Form from 'react-bootstrap/Form';
import { faCaretDown, faCaretUp, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import { useTheme } from 'styled-components';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { deleteAiConnector, AiConnector } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';

interface AiContextsTableProps {
        data: AiConnector[];
    setReload: (value: boolean) => void;
    setRowSelection: (selection: Record<string, boolean>) => void;
    rowSelection: Record<string, boolean>;
}

function AiContextsTable({ data, setReload, setRowSelection, rowSelection }: AiContextsTableProps) {
    const navigate = useNavigate();
    const [globalFilter, setGlobalFilter] = useState<string>("");
    const statusContext = useContext<StatusContextType>(StatusContext);
    const theme: any = useTheme();

    const handleDelete = async (id: string, description: string) => {
        if (window.confirm(`Delete connector "${description}"?`)) {
            try {
                await deleteAiConnector(id);
                statusContext.postInfo(`Connector "${description}" deleted successfully`);
                setReload(true);
            } catch (error: any) {
                statusContext.postError(`Failed to delete AI Connector: ${error.message}`);
            }
        }
    };

    const columns: ColumnDef<AiConnector>[] = [
        {
            id: 'select',
            header: ({ table }) => (
            <Form.Check
                type="checkbox"
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
            />
            ),
            cell: ({ row }) => (
                <Form.Check
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
        },
        {
            header: 'ID',
            accessorKey: 'id',
        },
        {
            header: 'Description',
            accessorKey: 'description',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <span
                        style={{ cursor: 'pointer' }}
                        className="text-primary"
                        onClick={() => {
                            navigate(`/ai/edit_connector/${row.id}`);
                        }}>
                        {row.description}
                    </span>
                );
            }
        },
        {
            header: 'Connector Type',
            id: 'vendor',
            cell: (props) => {
                const vendor = props.row.original.connection?.vendor;
                return vendor === 'perplexity_agent' ? 'Perplexity Research' : 'Perplexity Chat';
            }
        },
        {
            header: 'System Prompt',
            accessorKey: 'system_prompt',
            cell: (props) => {
                const value = props.getValue() as string;
                return value.length > 50 ? `${value.substring(0, 50)}...` : value;
            }
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div className="d-flex">
                        <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => navigate(`/ai/edit_connector/${row.id}`)}
                        >
                            <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(row.id, row.description)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                );
            }
        },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        globalFilterFn: 'includesString',
        enableGlobalFilter: true,
        enableMultiRowSelection: true,
        onRowSelectionChange: setRowSelection,
        getRowId: (row: any) => row.id.toString(),
        state: {
            rowSelection,
            globalFilter
        }
    });

    return (
        <Container>
            <div className="mb-3">
                <input
                    value={globalFilter ?? ''}
                    onChange={e => {
                        let newValue = String(e.target.value);
                        setGlobalFilter(newValue);
                        table.setGlobalFilter(newValue);
                    }}
                    className="form-control"
                    placeholder="Search connectors..."
                />
            </div>
            <BTable hover responsive size="sm"
                style={{ 
                    "--bs-table-bg": theme.colors.tableBg,
                    "--bs-table-color": theme.colors.ctext
                } as any}
            >
                <thead>{table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>{headerGroup.headers.map(header => (
                        <th key={header.id} colSpan={header.colSpan}>
                            <div onClick={header.column.getToggleSortingHandler()}>
                                {header.isPlaceholder ? null : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                                {header.column.getIsSorted() ? (
                                    <SortIcon>
                                        <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                    </SortIcon>
                                ) : null}
                            </div>
                        </th>
                    ))}</tr>
                ))}</thead>
                <tbody>{table.getRowModel().rows.map(row => (
                    <tr key={row.id} className={row.getIsSelected() ? 'table-info' : undefined}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}</tbody>
            </BTable>
        </Container>
    );
}

const SortIcon = styled.span`
    margin-left: 5px;
`;

export default AiContextsTable;
